import React, { useState } from "react";
import { Link } from "react-router-dom";
import {ProductionQuantityLimits} from "@mui/icons-material";

// ─────────────────────────────────────────────
//  Your First React Component
//  Chapter 1 · Intro
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `// Greeting.jsx  — simplest possible component
function Greeting() {
  return <h1>Hello, React! 👋</h1>;
}

// Arrow function syntax — equally valid
const Greeting = () => <h1>Hello, React! 👋</h1>;

// Always export so other files can import it
export default Greeting;`;

const CODE_2 = `// components/ProfileCard.jsx
import React from 'react';

function ProfileCard({ name, role, company, email, yearsExp, avatarUrl }) {
  const isSenior = yearsExp >= 5;

  return (
    <div style={{styles.card}}>
      {/* Avatar */}
      <img
        src={avatarUrl || 'https://via.placeholder.com/80'}
        alt={name}
        style={{styles.avatar}}
      />

      {/* Info */}
      <div style={{styles.info}}>
        <h3 style={{styles.name}}>
          {name}
          {isSenior && (
            <span style={{styles.badge}}>Senior</span>
          )}
        </h3>
        <p style={{styles.role}}>{role}</p>
        <p style={{styles.company}}>📍 {company}</p>
        <a href={\`mailto:\${email}\`} style={{styles.email}}>{email}</a>
      </div>
    </div>
  );
}

// Styles object
const styles = {
  card:    { display:'flex', gap:'16px', padding:'20px', borderRadius:'12px',
             border:'1px solid #e2e8f0', boxShadow:'0 2px 8px rgba(0,0,0,0.08)',
             backgroundColor:'#fff', maxWidth:'380px' },
  avatar:  { width:80, height:80, borderRadius:'50%', objectFit:'cover' },
  info:    { display:'flex', flexDirection:'column', gap:'4px' },
  name:    { margin:0, fontSize:'1.1rem', fontWeight:700, display:'flex',
             alignItems:'center', gap:'8px' },
  badge:   { fontSize:'0.65rem', backgroundColor:'#0070F3', color:'#fff',
             padding:'2px 8px', borderRadius:'999px', fontWeight:600 },
  role:    { margin:0, color:'#555', fontSize:'0.9rem' },
  company: { margin:0, color:'#888', fontSize:'0.85rem' },
  email:   { color:'#0070F3', fontSize:'0.85rem', textDecoration:'none' },
};

export default ProfileCard;`;

const CODE_3 = `// App.jsx
import ProfileCard from './components/ProfileCard';

function App() {
  return (
    <div style={{ padding: '40px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
      <ProfileCard
        name="Alice Johnson"
        role="Frontend Engineer"
        company="TechCorp"
        email="alice@techcorp.com"
        yearsExp={7}
        avatarUrl="https://i.pravatar.cc/80?img=1"
      />
      <ProfileCard
        name="Bob Smith"
        role="UX Designer"
        company="CreativeStudio"
        email="bob@creative.com"
        yearsExp={3}
        avatarUrl="https://i.pravatar.cc/80?img=2"
      />
    </div>
  );
}

export default App;`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function YourFirstReactComponent() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 1 · Intro</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Your First React Component</h1>
        <p style={styles.desc}>Build, export, and use your first real React component from scratch.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── What is a Component? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>What is a Component?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "A React component is a JavaScript function that returns JSX. Components are the fundamental building blocks of all React UIs — think of them as custom HTML elements you define yourself. Every button, card, modal, and page in your React app will be a component." }} />
      </div>

      /* ── The Simplest Component ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>The Simplest Component</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "A component is just a function with a capital first letter that returns JSX. The capital letter is how React distinguishes your custom components from built-in HTML elements." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── A Real-World Component ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>A Real-World Component</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Let&#39;s build a proper ProfileCard component with styles, conditional rendering, and good structure." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Using Components in App.jsx ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Using Components in App.jsx</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Import your component and use it like a custom HTML tag. Pass data as JSX attributes (props)." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_3, 2)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 2 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_3)}</pre>
        </div>
      </div>

      /* ── Component Best Practices ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Component Best Practices</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• One component per file — keeps code organised" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• PascalCase filenames: ProfileCard.jsx not profilecard.jsx" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Keep components focused — one job per component" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Extract sub-components when JSX exceeds ~50 lines" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Co-locate the component&#39;s styles in the same folder" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Export default for the main component, named exports for helpers" }} />
      </div>

      /* ── Practice Exercise ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Exercise</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE EXERCISE</div>
            <div style={styles.qItem}>✦ Build a <ProductionQuantityLimits> component that accepts: name (string), price (number), category (string), inStock (boolean), imageUrl (string). Display a \'Sold Out\' badge when inStock is false. Show the price formatted as currency. Render 4 different products in App.jsx.</ProductionQuantityLimits></div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
